import type { LootTable, LootTableLookup } from "./lootTable.js";

/**
 * I15a — module-singleton registry for authored loot tables.
 *
 * The resolver in `lootTable.ts` is pure + takes a `LootTableLookup`
 * callback; this registry is the default source of truth so containers
 * (and anything else that rolls loot by ID) can share a single table
 * pool. Adventures register their tables at boot via
 * `loadAdventureLootTables`.
 */
const tables = new Map<string, LootTable>();

export const LootTableRegistry = {
  register(table: LootTable): void {
    tables.set(table.id, table);
  },

  registerAll(list: readonly LootTable[]): void {
    for (const t of list) tables.set(t.id, t);
  },

  get(id: string): LootTable | undefined {
    return tables.get(id);
  },

  has(id: string): boolean {
    return tables.has(id);
  },

  /**
   * Every distinct item id any registered table can drop (guaranteed +
   * probabilistic rolls; `includes` targets are themselves registered
   * tables, so walking each table's own drops covers everything). Used
   * by the runtime to preload item records for the sync loot resolver.
   */
  allItemIds(): readonly string[] {
    const ids = new Set<string>();
    for (const t of tables.values()) {
      for (const d of t.guaranteed ?? []) ids.add(d.item);
      for (const roll of t.rolls ?? []) {
        for (const d of roll.drops) ids.add(d.item);
      }
    }
    return [...ids];
  },

  clear(): void {
    tables.clear();
  },

  /** Lookup callback suitable to pass into `resolveLootTable`. */
  lookup: ((id: string) => tables.get(id)) as LootTableLookup,
};
